import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import { formatRoleLabel } from '../utils/roleManagement';
import { formatDateTimeEST } from '../utils/dateTime';
import { MAX_PASSWORD_LENGTH, validatePassword } from '../utils/credentials';

const PROFILE_LABELS = {
  display_name: 'Display name',
  timezone: 'Timezone',
  country_region: 'Country / region',
  bio: 'Bio',
  favorite_formats: 'Favorite formats',
  email_notifications: 'Email notifications',
};

const SENSITIVE_LABELS = {
  email: 'Email',
  last_ip: 'Last known IP',
};

function SensitiveField({ label, value, canView }) {
  return (
    <label className="add-user-field user-edit-sensitive-field">
      <span className="add-user-label">
        {label}
        {!canView && <span className="user-edit-sensitive-tag"> (restricted)</span>}
      </span>
      <input
        type="text"
        value={value ?? ''}
        readOnly
        disabled
        className={['edit-user-readonly', !canView ? 'user-edit-masked' : ''].filter(Boolean).join(' ')}
        aria-label={canView ? label : `${label} (hidden)`}
      />
    </label>
  );
}

function UserEdit({ userId, onBack, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [user, setUser] = useState(null);
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [password, setPassword] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const response = await apiFetch(`/admin/users/${userId}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || 'Failed to load user');
        }
        if (cancelled) return;
        setUser(data);
        setAssignableRoles(data.assignable_roles || []);
        setRoleId(String(data.role_id ?? ''));
        setIsEnabled(!!data.is_enabled);
        setPassword('');
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load user');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    setError(null);
    setSuccess(null);

    const body = {
      role: roleId,
      is_enabled: isEnabled,
    };
    if (password.trim()) {
      const passwordError = validatePassword(password);
      if (passwordError) {
        setError(passwordError);
        return;
      }
      body.password = password;
    }

    setSaving(true);
    try {
      const response = await apiFetch(`/admin/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update user');
      }
      setSuccess('User updated successfully');
      setPassword('');
      if (data.user) {
        setUser((prev) => ({ ...prev, ...data.user }));
        setIsEnabled(!!data.user.is_enabled);
      }
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="user-edit-loading">Loading user…</p>;
  }

  if (error && !user) {
    return (
      <div className="edit-user-section">
        <div className="admin-form-message admin-form-message--error">{error}</div>
        <button type="button" className="user-edit-back-btn" onClick={onBack}>
          Back to user list
        </button>
      </div>
    );
  }

  const profile = user?.profile || {};
  const sensitive = user?.sensitive || {};
  const canViewSensitive = !!user?.can_view_sensitive;
  const knownProfileKeys = new Set(Object.keys(PROFILE_LABELS));
  const extraProfileEntries = Object.entries(profile).filter(([key]) => !knownProfileKeys.has(key));

  return (
    <div className="edit-user-section">
      <div className="edit-user-header">
        <button type="button" className="user-edit-back-btn" onClick={onBack}>
          ← Back
        </button>
        <h2>Edit User</h2>
      </div>

      {error && <div className="admin-form-message admin-form-message--error">{error}</div>}
      {success && <div className="admin-form-message admin-form-message--success">{success}</div>}

      <div className="edit-user-panel">
        <form className="edit-user-form" onSubmit={handleSubmit}>
          <label className="add-user-field">
            <span className="add-user-label">Username</span>
            <input type="text" value={user?.username || ''} readOnly disabled />
          </label>

          <div className="edit-user-meta-block">
            <h3>Account</h3>
            <div className="edit-user-meta-grid">
              <span>User ID: {user?.id}</span>
              <span>Role: {formatRoleLabel(user?.role || '')}</span>
              <span>Created: {formatDateTimeEST(user?.created_at)}</span>
              <span>Last login: {formatDateTimeEST(user?.last_login)}</span>
              <span>Last seen: {formatDateTimeEST(user?.last_seen_at)}</span>
            </div>
          </div>

          <div className="edit-user-contact-block">
            <h3>Club membership</h3>
            {user?.club_membership ? (
              <div className="edit-user-club-card">
                <strong>{user.club_membership.club_name}</strong>
                <span className="edit-user-club-slug">{user.club_membership.club_slug}</span>
                <div className="edit-user-club-meta">
                  Role: {user.club_membership.member_role}
                  {' · '}
                  Joined: {formatDateTimeEST(user.club_membership.joined_at)}
                </div>
              </div>
            ) : (
              <p className="edit-user-empty-note">Not in a club (clubless)</p>
            )}
          </div>

          <div className="edit-user-contact-block">
            <h3>Sensitive information</h3>
            {!canViewSensitive && (
              <p className="edit-user-sensitive-hint">
                Requires <code>view_sensitive_user_info</code> permission. Values are hidden with a fixed mask.
              </p>
            )}
            {Object.entries(SENSITIVE_LABELS).map(([key, label]) => (
              <SensitiveField
                key={key}
                label={label}
                value={sensitive[key]}
                canView={canViewSensitive}
              />
            ))}
          </div>

          <div className="edit-user-contact-block">
            <h3>Player profile (user-provided)</h3>
            {Object.entries(PROFILE_LABELS).map(([key, label]) => {
              const val = profile[key];
              const display = key === 'email_notifications'
                ? (val ? 'Yes' : 'No')
                : (val || '(not set)');
              return (
                <label key={key} className="add-user-field">
                  <span className="add-user-label">{label}</span>
                  {key === 'bio' ? (
                    <textarea
                      value={display}
                      readOnly
                      disabled
                      rows={3}
                      className="edit-user-readonly"
                    />
                  ) : (
                    <input
                      type="text"
                      value={display}
                      readOnly
                      disabled
                      className="edit-user-readonly"
                    />
                  )}
                </label>
              );
            })}
            {extraProfileEntries.length > 0 && (
              <div className="edit-user-extra-profile">
                <h4>Other profile fields</h4>
                {extraProfileEntries.map(([key, val]) => (
                  <label key={key} className="add-user-field">
                    <span className="add-user-label">{key}</span>
                    <input
                      type="text"
                      value={String(val ?? '(not set)')}
                      readOnly
                      disabled
                      className="edit-user-readonly"
                    />
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="edit-user-admin-block">
            <h3>Admin controls</h3>

            <label className="add-user-field">
              <span className="add-user-label">Role</span>
              {assignableRoles.length > 0 ? (
                <select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                  {assignableRoles.map((role) => (
                    <option key={role.role_id} value={role.role_id}>
                      {formatRoleLabel(role.role_name)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="add-user-role-placeholder">(No assignable roles)</div>
              )}
            </label>

            <label className="add-user-field">
              <span className="add-user-label">New password</span>
              <input
                type="password"
                placeholder="Leave blank to keep current password"
                value={password}
                onChange={(e) => setPassword(e.target.value.slice(0, MAX_PASSWORD_LENGTH))}
                autoComplete="new-password"
                maxLength={MAX_PASSWORD_LENGTH}
              />
              <span className="edit-user-hint">
                Set a new password when the user forgot theirs.
              </span>
            </label>

            <label className="edit-user-checkbox">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
              />
              <span>Account enabled</span>
            </label>
          </div>

          <button
            type="submit"
            className="add-user-submit"
            disabled={saving || !roleId}
          >
            {saving ? 'Saving…' : 'Save admin changes'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default UserEdit;